// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/home/TouristTaxDue.tsx
// 🔧 PURPOSE: La TASSA DI SOGGIORNO chiesta e non pagata, uguale nelle due
//             viste: prima dell'arrivo ("Da fare", vista B) e durante il
//             soggiorno ("Adesso per te", vista S). Si paga prima di
//             arrivare; il codice della porta lo attiva lo staff dopo il
//             pagamento, e la riga sotto lo dice.
//
//             Cambia solo il testo: B "Da pagare prima dell'arrivo · per N
//             ospiti · pagamento sicuro in app"; S "Non ancora pagata · da
//             pagare subito · N ospiti". Il foglio di pagamento sta in MyStay
//             (si apre da solo con ?open=tourist-tax); senza pagamento in app
//             si va lì comunque, alla card della tassa.
// ============================================

import React from 'react';
import { useTranslation } from 'react-i18next';
import type { GuestBooking } from '../../lib/guestApi';
import { formatEuro } from '../TouristTaxWidget';
import { DueBlock, InkButton } from './ui';

export interface TouristTaxDueProps {
  booking: GuestBooking;
  /** 'before' = vista B (prima dell'arrivo), 'stay' = vista S. */
  variant: 'before' | 'stay';
  /** h3 dentro "Da fare" (vista B), testo semplice fra le card della vista S. */
  titleAs?: 'span' | 'h3';
  className?: string;
}

const TouristTaxDue: React.FC<TouristTaxDueProps> = ({ booking, variant, titleAs = 'span', className }) => {
  const { t, i18n } = useTranslation();
  const tax = booking.touristTax;
  if (!tax || tax.status !== 'requested') return null;
  const locale = i18n.language || 'it';
  const apartments = tax.group?.apartments?.length || 0;
  const guests = booking.numberOfGuests > 0 ? booking.numberOfGuests : 0;

  let desc: string;
  if (variant === 'before') {
    const who = apartments > 0
      ? t('myStay.touristTax.forApartments', { count: apartments })
      : guests ? t('home.before.forGuests', { count: guests }) : '';
    desc = [who, tax.canPayInApp ? t('home.before.taxSecure') : ''].filter(Boolean).join(' · ');
  } else {
    const who = apartments > 0
      ? t('myStay.touristTax.forApartments', { count: apartments })
      : guests ? t('home.stay.guests', { count: guests }) : '';
    desc = [t('home.now.taxNotPaid'), who].filter(Boolean).join(' · ');
  }
  const payTo = tax.canPayInApp ? '/my-stay?open=tourist-tax' : '/my-stay';

  return (
    <DueBlock
      badge={variant === 'before' ? t('home.before.taxBadge') : t('home.now.taxBadge')}
      title={t('home.now.taxTitle')}
      titleAs={titleAs}
      desc={desc || undefined}
      amount={tax.amount > 0 ? formatEuro(tax.amount, locale) : undefined}
      action={<InkButton to={payTo} className="px-3">{t('home.now.taxCta')}</InkButton>}
      note={t('myStay.touristTax.codeAfterPayment')}
      className={className}
    />
  );
};

export default TouristTaxDue;
